import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { createOrder, updateOrder, deleteOrder } from "../utils/orderUtils";
import { getEquipment, updateEquipment } from "../utils/equipmentUtils";
import { getJobs } from "../utils/jobUtils";
import { getActions } from "../utils/actionUtils";
import { getContacts } from "../utils/contactUtils";
import CustomButton from "../components/common/CustomButton";
import CustomInput from "../components/common/CustomInput";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
  UserIcon,
  MapPinIcon,
  DocumentTextIcon,
  CalendarIcon,
  TrashIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  ClockIcon,
  TagIcon,
} from "@heroicons/react/24/outline";

const styles = {
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    minHeight: "100vh",
    padding: "16px 24px",
    paddingTop: 16,
    maxWidth: 1400,
    margin: "0 auto",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    animation: "fadeIn 0.5s ease-in-out",
  },
  title: {
    fontSize: 32,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    fontWeight: 700,
  },
  content: {
    maxWidth: 500,
    margin: "0 auto",
  },
  toggleContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  toggleButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    border: `1px solid ${COLORS.primary}`,
    alignItems: "center",
    margin: "0 4px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: `${COLORS.primary}20`,
    },
  },
  selectedToggle: {
    backgroundColor: COLORS.primary,
    "&:hover": {
      backgroundColor: COLORS.primary,
    },
  },
  toggleText: {
    fontSize: 16,
    fontFamily: FONTS.bold,
    color: COLORS.text,
  },
  selectedToggleText: {
    color: COLORS.white,
  },
  sectionLabel: {
    fontSize: 18,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    marginBottom: 12,
  },
  equipmentList: {
    display: "flex",
    overflowX: "auto",
    padding: "8px 0",
    marginBottom: 12,
  },
  equipmentItem: {
    width: 100,
    marginRight: 12,
    alignItems: "center",
    cursor: "pointer",
    padding: 8,
    borderRadius: 12,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: `1px solid ${COLORS.tertiary}`,
    transition: "all 0.3s ease",
    position: "relative",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& img, & div, & span": {
        borderColor: COLORS.white,
        color: COLORS.white,
      },
    },
  },
  selectedEquipment: {
    backgroundColor: `${COLORS.primary}15`,
    border: `2px solid ${COLORS.primary}`,
  },
  rentedBadge: {
    position: "absolute",
    top: 4,
    right: 4,
    backgroundColor: `${COLORS.secondary}80`,
    borderRadius: 8,
    padding: "2px 6px",
    border: `1px solid ${COLORS.tertiary}`,
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
  },
  rentedBadgeText: {
    fontSize: 10,
    color: COLORS.error,
    fontFamily: FONTS.regularInter,
  },
  equipmentImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
    objectFit: "cover",
    border: `1px solid ${COLORS.tertiary}`,
  },
  noImagePlaceholder: {
    width: 80,
    height: 80,
    backgroundColor: COLORS.gray,
    borderRadius: 8,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: `1px solid ${COLORS.tertiary}`,
  },
  noImageText: {
    fontSize: 12,
    color: COLORS.text,
    textAlign: "center",
    fontFamily: FONTS.regularInter,
  },
  equipmentName: {
    fontSize: 14,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    textAlign: "center",
    marginTop: 8,
    maxWidth: 80,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
    wordWrap: "break-word",
    lineHeight: "1.2em",
    maxHeight: "2.4em",
  },
  select: {
    width: "100%",
    height: 56,
    padding: "0 12px",
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    marginBottom: 12,
    "&:hover": {
      borderColor: COLORS.primary,
      boxShadow: `0 4px 12px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  datePickerContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: 56,
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    backgroundColor: COLORS.white,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    marginBottom: 12,
    "&:hover": {
      borderColor: COLORS.primary,
    },
  },
  datePicker: {
    flex: 1,
    height: 56,
    padding: 12,
    border: "none",
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    backgroundColor: "transparent",
    outline: "none",
  },
  datePickerText: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    padding: "0 12px",
    width: 120,
  },
  descriptionInput: {
    height: 120,
    marginBottom: 12,
  },
  notesInput: {
    height: 120,
    marginBottom: 12,
  },
  priceContainer: {
    marginBottom: 12,
    padding: 12,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    display: "flex",
    flexDirection: "column",
  },
  priceField: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  priceIcon: {
    width: 20,
    height: 20,
    color: COLORS.primary,
  },
  priceText: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.bold,
    flex: 1,
  },
  priceDetailText: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    flex: 1,
  },
  saveButton: {
    marginTop: 12,
    marginBottom: 32,
    borderRadius: 12,
  },
  deleteButton: {
    padding: 10,
    borderRadius: 12,
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.error,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  alert: {
    padding: 12,
    backgroundColor: COLORS.error,
    color: COLORS.white,
    borderRadius: 12,
    marginBottom: 12,
    fontFamily: FONTS.regularInter,
    fontSize: 16,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  alertCloseButton: {
    cursor: "pointer",
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    content: {
      maxWidth: 400,
    },
    select: {
      height: 48,
    },
    datePickerContainer: {
      height: 48,
    },
    datePicker: {
      padding: 10,
      fontSize: 14,
    },
    datePickerText: {
      fontSize: 14,
      padding: "0 10px",
    },
    equipmentItem: {
      width: 80,
    },
    equipmentImage: {
      width: 64,
      height: 64,
    },
    noImagePlaceholder: {
      width: 64,
      height: 64,
    },
    equipmentName: {
      fontSize: 12,
      maxWidth: 64,
      lineHeight: "1.1em",
      maxHeight: "2.2em",
    },
    rentedBadgeText: {
      fontSize: 8,
    },
    toggleButton: {
      padding: 10,
    },
    toggleText: {
      fontSize: 14,
    },
    priceText: {
      fontSize: 14,
    },
    priceDetailText: {
      fontSize: 14,
    },
  },
};

const CreateOrderScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = location.pathname.includes("/edit");
  const order = location.state?.order;

  const [clientName, setClientName] = useState("");
  const [contactList, setContactList] = useState([]);
  const [address, setAddress] = useState("");
  const [equipmentId, setEquipmentId] = useState("");
  const [jobId, setJobId] = useState("");
  const [actionId, setActionId] = useState("");
  const [unit, setUnit] = useState("");
  const [quantity, setQuantity] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [status, setStatus] = useState("Pending");
  const [pendingDate, setPendingDate] = useState(null);
  const [completedDate, setCompletedDate] = useState(null);
  const [equipmentList, setEquipmentList] = useState([]);
  const [jobList, setJobList] = useState([]);
  const [actionList, setActionList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [selectedMode, setSelectedMode] = useState("equipment");
  const [priceDetails, setPriceDetails] = useState({
    totalPrice: "To be calculated",
    duration: "0.0 days",
    pricingType: "To be calculated",
  });

  const statusOptions = ["Pending", "Active", "Completed", "Cancelled"];

  useEffect(() => {
    return () => setAlertMessage(null);
  }, []);

  useEffect(() => {
    if (order && isEditMode) {
      if (!order.id) {
        setAlertMessage("Invalid order data: No order ID provided.");
        return;
      }
      setClientName(order.clientName || "");
      setAddress(order.address || "");
      setEquipmentId(order.equipmentId || "");
      setJobId(order.jobId || "");
      setActionId(order.actionId || "");
      setUnit(order.unit || "");
      setQuantity(order.quantity?.toString() || "");
      setDescription(order.description || "");
      setNotes(order.notes || "");
      setStartDate(order.startDate ? new Date(order.startDate) : null);
      setEndDate(
        order.approximateEndDate ? new Date(order.approximateEndDate) : null
      );
      setStatus(order.status || "Pending");
      setPendingDate(order.pendingDate ? new Date(order.pendingDate) : null);
      setCompletedDate(
        order.completedDate ? new Date(order.completedDate) : null
      );
      setSelectedMode(order.equipmentId ? "equipment" : "action");
    } else if (isEditMode && !order) {
      setAlertMessage(
        "No order data provided for editing. Please select an order from the list."
      );
      navigate("/create-order", { replace: true });
    }
  }, [order, isEditMode, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [equipment, jobs, actions, contacts] = await Promise.all([
          getEquipment(user.uid),
          getJobs(),
          getActions(),
          getContacts(),
        ]);
        const cleanContacts = contacts
          .map((contact) => ({
            id: contact.id,
            name: contact.name || "Unnamed Contact",
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setEquipmentList(
          equipment.sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        );
        setJobList(
          jobs
            .filter((job) => job.roles?.isEquipmentMan)
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        );
        setActionList(
          actions.sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        );
        setContactList(cleanContacts);
      } catch (error) {
        console.error("Failed to fetch data:", error);
        setAlertMessage(
          "Failed to fetch equipment, jobs, actions, or contacts"
        );
      }
    };
    fetchData();
  }, [user.uid]);

  useEffect(() => {
    if (status === "Pending" && !pendingDate) {
      setPendingDate(new Date());
    } else if (status === "Completed" && !completedDate) {
      setCompletedDate(new Date());
    } else if (status !== "Pending") {
      setPendingDate(null);
    } else if (status !== "Completed") {
      setCompletedDate(null);
    }
  }, [status, pendingDate, completedDate]);

  useEffect(() => {
    if (selectedMode === "equipment") {
      if (!equipmentId || !jobId || !startDate) {
        setPriceDetails({
          totalPrice: "To be calculated",
          duration: "0.0 days",
          pricingType: equipmentList.find((item) => item.id === equipmentId)
            ?.prices?.daily
            ? `Daily ($${
                equipmentList.find((item) => item.id === equipmentId)?.prices
                  .daily
              }/day)`
            : "To be calculated",
        });
        return;
      }

      const equipment = equipmentList.find((item) => item.id === equipmentId);
      const job = jobList.find((item) => item.id === jobId);
      if (!equipment || !equipment.prices || !job) {
        setPriceDetails({
          totalPrice: "To be calculated",
          duration: "0.0 days",
          pricingType: "To be calculated",
        });
        return;
      }

      const start = new Date(startDate);
      const end = endDate ? new Date(endDate) : new Date();
      const MS_HOUR = 1000 * 60 * 60;
      const MS_DAY = MS_HOUR * 24;

      const DAYS_IN_WEEK = 7;
      const DAYS_IN_MONTH = 30;
      const WEEK_THRESHOLD_DAYS = 3.5;
      const MONTH_THRESHOLD_DAYS = 2.7 * 7;

      const dailyPrice = equipment.prices.daily;
      const weeklyPrice = equipment.prices.weekly;
      const monthlyPrice = equipment.prices.monthly;

      const durationMs = end - start;
      if (durationMs <= 0) {
        setPriceDetails({
          totalPrice: "To be calculated",
          duration: "0.0 days",
          pricingType: `Daily ($${dailyPrice}/day)`,
        });
        return;
      }

      const totalHours = durationMs / MS_HOUR;
      const totalDaysRaw = durationMs / MS_DAY;

      const fullDays = Math.floor(totalDaysRaw);
      const remainingHours = totalHours - fullDays * 24;

      const extraDay = remainingHours > 4 ? 1 : 0;
      let totalDays = fullDays + extraDay;

      let months = Math.floor(totalDays / DAYS_IN_MONTH);
      let remainderAfterMonths = totalDays - months * DAYS_IN_MONTH;

      if (remainderAfterMonths >= MONTH_THRESHOLD_DAYS) {
        months += 1;
        remainderAfterMonths = 0;
      }

      let weeks = 0;
      let days = 0;

      if (remainderAfterMonths >= WEEK_THRESHOLD_DAYS) {
        weeks = Math.ceil(remainderAfterMonths / DAYS_IN_WEEK);
        remainderAfterMonths = 0;
      }

      if (remainderAfterMonths > 0) {
        days = Math.floor(remainderAfterMonths);
        if (days === 0 && remainderAfterMonths > 0) {
          days = 1;
        }
      }

      if (months === 0 && weeks === 0 && days === 0) {
        days = 1;
      }

      const totalPriceNumber =
        months * monthlyPrice + weeks * weeklyPrice + days * dailyPrice;

      const parts = [];
      if (months) parts.push(`Monthly ($${monthlyPrice}/month) × ${months}`);
      if (weeks) parts.push(`Weekly ($${weeklyPrice}/week) × ${weeks}`);
      if (days) parts.push(`Daily ($${dailyPrice}/day) × ${days}`);
      const pricingType = parts.join(", ");

      setPriceDetails({
        totalPrice: totalPriceNumber.toFixed(2),
        duration: `${totalDaysRaw.toFixed(1)} days`,
        pricingType,
      });
    } else if (selectedMode === "action" && actionId && unit && quantity) {
      const action = actionList.find((item) => item.id === actionId);
      if (action && action.prices && action.prices[unit]) {
        const unitPrice = parseFloat(action.prices[unit]);
        if (!isNaN(unitPrice)) {
          const totalPriceNumber = (parseFloat(quantity) * unitPrice).toFixed(
            2
          );
          const capitalizedUnit = unit.charAt(0).toUpperCase() + unit.slice(1);
          setPriceDetails({
            totalPrice: totalPriceNumber,
            duration: "",
            pricingType: `${capitalizedUnit} ($${unitPrice}/${unit}) × ${quantity}`,
          });
        } else {
          setPriceDetails({
            totalPrice: "To be calculated",
            duration: "",
            pricingType: "To be calculated",
          });
        }
      } else {
        setPriceDetails({
          totalPrice: "To be calculated",
          duration: "",
          pricingType: "To be calculated",
        });
      }
    } else {
      setPriceDetails({
        totalPrice: "To be calculated",
        duration: "",
        pricingType: "To be calculated",
      });
    }
  }, [
    selectedMode,
    equipmentId,
    jobId,
    startDate,
    endDate,
    actionId,
    unit,
    quantity,
    equipmentList,
    jobList,
    actionList,
  ]);

  const handleDelete = async () => {
    if (!isEditMode || !order?.id) {
      setAlertMessage("Cannot delete: No valid order ID provided.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this order?")) {
      setLoading(true);
      try {
        await deleteOrder(order.id);
        if (order.equipmentId) {
          await updateEquipment(order.equipmentId, { status: "available" });
        }
        setAlertMessage("Order deleted successfully");
        navigate("/create-order");
      } catch (error) {
        console.error("Failed to delete order:", error.message, error.stack);
        setAlertMessage(`Failed to delete order: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    if (
      !clientName ||
      !jobId ||
      !description ||
      (selectedMode === "equipment" && !equipmentId) ||
      (selectedMode === "action" && (!actionId || !unit || !quantity))
    ) {
      setAlertMessage(
        "Please fill in all required fields: Contact, Job, Description" +
          (selectedMode === "equipment"
            ? ", and Equipment"
            : ", Action, Unit, and Quantity")
      );
      return;
    }

    if (selectedMode === "equipment") {
      if (!isEditMode) {
        const today = moment().startOf("day");
        const selectedStartDate = moment(startDate).startOf("day");
        if (selectedStartDate.isBefore(today)) {
          setAlertMessage("Start date cannot be in the past for new orders");
          return;
        }
      }

      if (endDate && moment(endDate).isBefore(moment(startDate))) {
        setAlertMessage("End date must be after start date");
        return;
      }
    }

    if (status === "Completed" && (!pendingDate || !completedDate)) {
      setAlertMessage(
        "Pending and Completed dates are required for Completed status"
      );
      return;
    }

    setLoading(true);
    try {
      const equipment =
        selectedMode === "equipment"
          ? equipmentList.find((item) => item.id === equipmentId)
          : null;
      const job = jobList.find((item) => item.id === jobId);
      const action = actionList.find((item) => item.id === actionId);
      const orderData = {
        userId: user.uid,
        equipmentId: selectedMode === "equipment" ? equipmentId : null,
        equipmentName: equipment?.name || "",
        jobId,
        jobName: job?.name || "",
        jobNumber: job?.jobNumber || "",
        actionId: selectedMode === "action" ? actionId : null,
        actionName: selectedMode === "action" ? action?.name || "" : "",
        unit: selectedMode === "action" ? unit : null,
        quantity: selectedMode === "action" ? parseFloat(quantity) || 0 : 0,
        contactName: clientName,
        address,
        description,
        notes,
        price:
          priceDetails.totalPrice === "To be calculated"
            ? 0
            : parseFloat(priceDetails.totalPrice),
        startDate:
          selectedMode === "equipment" ? startDate?.toISOString() : null,
        approximateEndDate:
          selectedMode === "equipment" ? endDate?.toISOString() : null,
        status,
        pendingDate: pendingDate ? pendingDate.toISOString() : null,
        completedDate: completedDate ? completedDate.toISOString() : null,
        createdAt: order ? order.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        equipmentPrices:
          selectedMode === "equipment" && equipment
            ? {
                daily: equipment.prices?.daily ?? 0,
                weekly: equipment.prices?.weekly ?? 0,
                monthly: equipment.prices?.monthly ?? 0,
                totalCost: equipment.prices?.totalCost ?? 0,
              }
            : null,
      };

      console.log("Saving order with data:", orderData);

      if (isEditMode) {
        if (!order?.id) {
          throw new Error("Order ID is missing for update");
        }
        await updateOrder(order.id, orderData);
        if (selectedMode === "equipment" && equipmentId) {
          if (
            ["Completed", "Cancelled"].includes(status) &&
            order.status !== status
          ) {
            await updateEquipment(equipmentId, { status: "available" });
          } else if (order.status !== status) {
            await updateEquipment(equipmentId, { status: "rented out" });
          }
        }
        setAlertMessage("Order updated successfully");
      } else {
        await createOrder(orderData);
        if (selectedMode === "equipment" && equipmentId) {
          await updateEquipment(equipmentId, { status: "rented out" });
        }
        setAlertMessage("Order created successfully");
      }
      navigate("/create-order?refresh=true");
    } catch (error) {
      console.error("Failed to save order:", error.message, error.stack);
      setAlertMessage(`Failed to save order: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderEquipmentItem = (item) => (
    <div
      style={{
        ...styles.equipmentItem,
        ...(equipmentId === item.id ? styles.selectedEquipment : {}),
      }}
      key={item.id}
      onClick={() => setEquipmentId(item.id)}
    >
      {item.status === "rented out" && (
        <div style={styles.rentedBadge}>
          <span style={styles.rentedBadgeText}>Rented</span>
        </div>
      )}
      {item.images && item.images.length > 0 ? (
        <img
          src={item.images[0]}
          alt={item.name || "Equipment"}
          style={styles.equipmentImage}
        />
      ) : (
        <div style={styles.noImagePlaceholder}>
          <span style={styles.noImageText}>No Image</span>
        </div>
      )}
      <span style={styles.equipmentName}>{item.name || "Unnamed"}</span>
    </div>
  );

  const renderContactPicker = () => (
    <select
      value={clientName}
      onChange={(e) => setClientName(e.target.value)}
      style={styles.select}
    >
      <option value="">Select Contact</option>
      {contactList.map((contact) => (
        <option key={contact.id} value={contact.name}>
          {contact.name}
        </option>
      ))}
    </select>
  );

  const renderJobPicker = () => (
    <select
      value={jobId}
      onChange={(e) => setJobId(e.target.value)}
      style={styles.select}
    >
      <option value="">Select Job</option>
      {jobList.map((job) => (
        <option key={job.id} value={job.id}>
          {job.jobNumber ? `${job.jobNumber} - ${job.name}` : job.name}
        </option>
      ))}
    </select>
  );

  const renderActionPicker = () => (
    <select
      value={actionId}
      onChange={(e) => {
        setActionId(e.target.value);
        setUnit("");
        setQuantity("");
      }}
      style={styles.select}
    >
      <option value="">Select Action</option>
      {actionList.map((action) => (
        <option key={action.id} value={action.id}>
          {action.name}
        </option>
      ))}
    </select>
  );

  const renderUnitPicker = () => {
    if (!actionId) return null;
    const action = actionList.find((a) => a.id === actionId);
    if (!action || !action.prices) return null;
    const availableUnits = Object.keys(action.prices).filter(
      (unit) => action.prices[unit] !== "" && action.prices[unit] !== undefined
    );
    if (availableUnits.length === 0) return null;

    return (
      <select
        value={unit}
        onChange={(e) => {
          setUnit(e.target.value);
          setQuantity("");
        }}
        style={styles.select}
        disabled={!actionId}
      >
        <option value="">Select Unit</option>
        {availableUnits.map((unit) => (
          <option key={unit} value={unit}>
            {unit.charAt(0).toUpperCase() + unit.slice(1)}
          </option>
        ))}
      </select>
    );
  };

  const renderStatusPicker = () => (
    <select
      value={status}
      onChange={(e) => setStatus(e.target.value)}
      style={styles.select}
    >
      {statusOptions.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );

  const CustomDateInput = ({ value, onClick, placeholder }) => (
    <div style={styles.datePickerContainer}>
      <CalendarIcon width={24} height={24} stroke={COLORS.primary} />
      <input
        type="text"
        value={value}
        onClick={onClick}
        readOnly
        style={styles.datePicker}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div style={styles.safeArea}>
      <div style={styles.container}>
        {alertMessage && (
          <div style={styles.alert}>
            <span>{alertMessage}</span>
            <button
              style={styles.alertCloseButton}
              onClick={() => setAlertMessage(null)}
            >
              <XMarkIcon width={20} height={20} stroke={COLORS.white} />
            </button>
          </div>
        )}
        <div style={styles.headerContainer}>
          <h1 style={styles.title}>
            {isEditMode ? "Edit Order" : "Create Order"}
          </h1>
          {isEditMode && (
            <button style={styles.deleteButton} onClick={handleDelete}>
              <TrashIcon width={24} height={24} stroke={COLORS.error} />
            </button>
          )}
        </div>
        <div style={styles.content}>
          <div style={styles.toggleContainer}>
            <button
              style={{
                ...styles.toggleButton,
                ...(selectedMode === "equipment" ? styles.selectedToggle : {}),
              }}
              onClick={() => {
                setSelectedMode("equipment");
                setActionId("");
                setUnit("");
                setQuantity("");
                setPriceDetails({
                  totalPrice: "To be calculated",
                  duration: "0.0 days",
                  pricingType: "To be calculated",
                });
              }}
            >
              <span
                style={{
                  ...styles.toggleText,
                  ...(selectedMode === "equipment"
                    ? styles.selectedToggleText
                    : {}),
                }}
              >
                Equipment
              </span>
            </button>
            <button
              style={{
                ...styles.toggleButton,
                ...(selectedMode === "action" ? styles.selectedToggle : {}),
              }}
              onClick={() => {
                setSelectedMode("action");
                setEquipmentId("");
                setStartDate(null);
                setEndDate(null);
                setPriceDetails({
                  totalPrice: "To be calculated",
                  duration: "",
                  pricingType: "To be calculated",
                });
              }}
            >
              <span
                style={{
                  ...styles.toggleText,
                  ...(selectedMode === "action"
                    ? styles.selectedToggleText
                    : {}),
                }}
              >
                Action
              </span>
            </button>
          </div>
          {renderContactPicker()}
          {selectedMode === "equipment" && (
            <>
              <span style={styles.sectionLabel}>Equipment</span>
              <div style={styles.equipmentList}>
                {equipmentList.map(renderEquipmentItem)}
              </div>
            </>
          )}
          {renderJobPicker()}
          {selectedMode === "action" && (
            <>
              {renderActionPicker()}
              {renderUnitPicker()}
              <CustomInput
                value={quantity}
                onChangeText={setQuantity}
                icon="document-text"
                placeholder="Enter quantity"
                type="number"
                style={styles.descriptionInput}
              />
            </>
          )}
          {/* <CustomInput
            value={description}
            onChangeText={setDescription}
            icon="document-text"
            placeholder="Enter order description"
            multiline
            style={styles.descriptionInput}
          /> */}
          <CustomInput
            value={notes}
            onChangeText={setNotes}
            icon="document-text"
            placeholder="Enter additional notes"
            multiline
            style={styles.notesInput}
          />
          {selectedMode === "equipment" && (
            <>
              <div style={styles.datePickerContainer}>
                <span style={styles.datePickerText}>Start Date</span>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  dateFormat="MM/dd/yyyy HH:mm"
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  placeholderText="Start date and time"
                  minDate={isEditMode ? null : new Date()}
                  customInput={
                    <CustomDateInput placeholder="Start date and time" />
                  }
                  withPortal={false}
                />
              </div>
              <div style={styles.datePickerContainer}>
                <span style={styles.datePickerText}>End Date</span>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  dateFormat="MM/dd/yyyy HH:mm"
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  placeholderText="End date and time"
                  customInput={
                    <CustomDateInput placeholder="End date and time" />
                  }
                  withPortal={false}
                />
              </div>
            </>
          )}
          <span style={styles.sectionLabel}>Status</span>
          {renderStatusPicker()}
          {(selectedMode === "equipment" && equipmentId && jobId) ||
          (selectedMode === "action" && actionId && unit && quantity) ? (
            <div style={styles.priceContainer}>
              <div style={styles.priceField}>
                <CurrencyDollarIcon style={styles.priceIcon} />
                <span style={styles.priceText}>
                  Calculated Price: ${priceDetails.totalPrice}
                </span>
              </div>
              {selectedMode === "equipment" && (
                <div style={styles.priceField}>
                  <ClockIcon style={styles.priceIcon} />
                  <span style={styles.priceDetailText}>
                    Duration: {priceDetails.duration}
                  </span>
                </div>
              )}
              <div style={styles.priceField}>
                <TagIcon style={styles.priceIcon} />
                <span style={styles.priceDetailText}>
                  Pricing Type: {priceDetails.pricingType}
                </span>
              </div>
            </div>
          ) : null}
          <CustomButton
            title={loading ? "Saving..." : isEditMode ? "Update Order" : "Save"}
            onPress={handleSave}
            style={styles.saveButton}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateOrderScreen;
